import ensureError from "@-/com.npmjs.ensure-error";
import { View } from "@-/com.npmjs.react-native";
import { useMutation, useQueries, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  ToolbarPopoverActionButton,
  ToolbarPopoverButton,
  ToolbarPopoverSection,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.popover";
import {
  CHINA_MAP_REGION_IDS,
  type ChinaMapRegionId,
} from "@-/dev.improve.haniverse.data.china-map";
import {
  createMapDownloadService,
  mapDownloadQueryKey,
} from "@-/dev.improve.haniverse.lib.map-downloads";
import { useCallback, useRef } from "react";
import { CHINA_MAP_REGION_COPY } from "#china-map-region-copy";

/** Keep download state in the page shell while map regions/content mount and unmount. */
export function useMapOfflineDownloads({
  enabled,
  service,
  workerUrl,
}: {
  /** Off until the map is opened: the map's shell is mounted ahead of time, beneath Learn's cards. */
  enabled: boolean;
  service: ReturnType<typeof createMapDownloadService>;
  workerUrl: string;
}) {
  const queryClient = useQueryClient();
  const controller = useRef<AbortController | null>(null);
  const refreshInfoOnOpen = useCallback(
    (open: boolean) => {
      if (open) {
        void queryClient.invalidateQueries({ queryKey: ["offline-map-info", workerUrl] });
      }
    },
    [queryClient, workerUrl],
  );
  const statuses = useQueries({
    queries: CHINA_MAP_REGION_IDS.map((regionId) => ({
      enabled,
      networkMode: "always" as const,
      queryFn: () => service.info(regionId),
      queryKey: ["offline-map-info", workerUrl, regionId],
    })),
  });
  const mutation = useMutation({
    mutationFn: async ({
      action,
      regionId,
    }: {
      action: "download" | "remove";
      regionId: ChinaMapRegionId;
    }) => {
      if (action === "remove") {
        await service.remove(regionId);
        // Clear transient geometry too, so removal cannot masquerade as offline availability.
        await queryClient.cancelQueries({ queryKey: mapDownloadQueryKey(workerUrl, regionId) });
        // The map's bounded query owns its subsequent online load; removal itself is complete.
        void queryClient
          .resetQueries({ queryKey: mapDownloadQueryKey(workerUrl, regionId) })
          .catch(() => {});
      } else {
        const downloadController = new AbortController();
        controller.current = downloadController;
        try {
          const pack = await service.download(regionId, downloadController.signal);
          queryClient.setQueryData(mapDownloadQueryKey(workerUrl, regionId), pack);
        } finally {
          controller.current = null;
        }
      }
    },
    mutationKey: ["offline-map-action", workerUrl],
    networkMode: "always",
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["offline-map-info", workerUrl] });
    },
  });
  const pending = mutation.isPending;
  const cancelled = mutation.error !== null && ensureError(mutation.error).name === "AbortError";

  return (
    <ToolbarPopoverButton
      accessibilityLabel="Offline maps"
      label="Offline maps"
      name="MdSystemUpdate"
      onOpenChange={refreshInfoOnOpen}
      testID="map-offline-maps-button"
    >
      <ToolbarPopoverSection title="Offline maps">
        <Text className="text-xs text-muted-foreground">
          Maps use the internet unless downloaded. Save a map on this device to use it offline.
          Downloads include travel timetables.
        </Text>
        {CHINA_MAP_REGION_IDS.map((regionId, index) => {
          const info = statuses[index]?.data;
          const downloading =
            pending &&
            mutation.variables?.regionId === regionId &&
            mutation.variables.action === "download";
          return (
            <View className="gap-1.5" key={regionId}>
              <Text className="text-sm font-medium text-foreground">
                {CHINA_MAP_REGION_COPY[regionId].label}
              </Text>
              <Text
                className="text-xs text-muted-foreground"
                testID={`map-download-status-${regionId}`}
              >
                {downloading
                  ? "Downloading…"
                  : info == null
                    ? "Not downloaded"
                    : `Available offline · ${(info.bytes / 1_000_000).toFixed(1)} MB · ${new Date(info.downloadedAt).toLocaleDateString()}`}
              </Text>
              <ToolbarPopoverActionButton
                closeOnPress={false}
                disabled={pending}
                isLoading={downloading}
                label={info == null ? "Download map" : "Update download"}
                name="MdSystemUpdate"
                onPress={() => mutation.mutate({ action: "download", regionId })}
                testID={`map-download-${regionId}`}
              />
              {info == null ? null : (
                <ToolbarPopoverActionButton
                  closeOnPress={false}
                  disabled={pending}
                  label="Remove download"
                  name="MdDeleteOutline"
                  onPress={() => mutation.mutate({ action: "remove", regionId })}
                  testID={`map-remove-download-${regionId}`}
                />
              )}
            </View>
          );
        })}
        {pending && mutation.variables?.action === "download" ? (
          <ToolbarPopoverActionButton
            closeOnPress={false}
            label="Cancel download"
            onPress={() => controller.current?.abort()}
            testID="map-cancel-download"
          />
        ) : null}
        {cancelled || mutation.error === null ? null : (
          <Text
            accessibilityRole="alert"
            className="text-xs text-destructive"
            testID="map-download-error"
          >
            {ensureError(mutation.error).message}
          </Text>
        )}
        {statuses.some((status) => status.isError) ? (
          <Text className="text-xs text-destructive">
            Offline storage could not be read. Close and reopen this menu to retry.
          </Text>
        ) : null}
      </ToolbarPopoverSection>
    </ToolbarPopoverButton>
  );
}
